'use client';
import React, { useState, useEffect } from 'react';
import { entryService } from '@/lib/supabaseService';
import { usePersistedClimbs } from '@/lib/usePersistedClimbs';
import { type ClimbEntry } from '@/lib/climbData';
import LeaderboardKPIs from './LeaderboardKPIs';
import ClimbTabs from './ClimbTabs';
import ClimbStatsHeader from './ClimbStatsHeader';
import RankingTable from './RankingTable';
import TimeDistributionChart from './TimeDistributionChart';
import Icon from '@/components/ui/AppIcon';

export default function LeaderboardPage() {
  const climbs = usePersistedClimbs();
  const [activeClimbId, setActiveClimbId] = useState('climb-stelvio');
  const [searchQuery, setSearchQuery] = useState('');
  const [allEntries, setAllEntries] = useState<ClimbEntry[]>([]);
  const [loadingEntries, setLoadingEntries] = useState(true);

  useEffect(() => {
    entryService.getAll().then((data) => {
      setAllEntries(data);
    }).catch(() => {
      setAllEntries([]);
    }).finally(() => setLoadingEntries(false));
  }, []);

  const activeClimb = climbs?.find((c) => c?.id === activeClimbId) ?? climbs?.[0];
  const climbEntries = allEntries?.filter(
    (e) => e?.climbId === activeClimbId && e?.status === 'approved'
  )?.sort((a, b) => a?.timeSeconds - b?.timeSeconds);

  const leader = climbEntries?.[0];

  return (
    <div className="py-8">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Icon name="TrophyIcon" size={22} className="text-accent" />
            <h1 className="text-3xl font-extrabold text-foreground">
              Classifica <span className="text-gradient-orange">IA Climb</span>
            </h1>
          </div>
          <p className="text-sm text-muted-foreground">
            9 salite iconiche · Tempi verificati da Strava · Stagione 2026
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Icon
              name="MagnifyingGlassIcon"
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              type="text"
              placeholder="Cerca ciclista..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="input-field pl-9 pr-4 py-2.5 text-sm w-52 rounded-lg"
            />
          </div>
          <a
            href="/sign-up-login-screen"
            className="btn-primary px-4 py-2.5 rounded-lg text-sm font-semibold flex items-center gap-2"
          >
            <Icon name="PlusIcon" size={16} />
            Importa Tempo
          </a>
        </div>
      </div>
      {/* KPIs */}
      <LeaderboardKPIs />
      {/* Climb tabs */}
      <ClimbTabs
        climbs={climbs}
        activeClimbId={activeClimbId}
        onSelect={(id) => { setActiveClimbId(id); setSearchQuery(''); }}
      />
      {/* Climb stats header */}
      <ClimbStatsHeader
        climb={activeClimb}
        participantCount={climbEntries?.length}
        leaderTime={leader?.timeFormatted ?? '—'}
        leaderName={leader?.userName ?? '—'}
      />
      {/* Main content: table + chart */}
      {loadingEntries ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2">
            <RankingTable entries={allEntries?.filter((e) => e?.climbId === activeClimbId)} searchQuery={searchQuery} />
          </div>
          <div className="xl:col-span-1 flex flex-col gap-4">
            <TimeDistributionChart
              entries={allEntries?.filter((e) => e?.climbId === activeClimbId)}
              climbName={activeClimb?.name}
            />
            {/* Quick climb overview */}
            <div className="bg-card border border-border rounded-xl p-5 card-glow">
              <h3 className="text-sm font-semibold text-foreground mb-3">Tutte le Salite</h3>
              <div className="space-y-2">
                {climbs?.map((climb) => {
                  const count = allEntries?.filter(
                    (e) => e?.climbId === climb?.id && e?.status === 'approved'
                  )?.length;
                  const pct = Math.min((count / 12) * 100, 100);
                  const isActive = climb?.id === activeClimbId;
                  return (
                    <button
                      key={`overview-${climb?.id}`}
                      onClick={() => { setActiveClimbId(climb?.id); setSearchQuery(''); }}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-all duration-150 ${
                        isActive ? 'bg-primary/10 border border-primary/30' : 'hover:bg-muted border border-transparent'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs font-semibold truncate ${isActive ? 'text-primary' : 'text-foreground'}`}>
                          {climb?.name}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex-1 h-1 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full progress-bar"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <span className="text-xs text-muted-foreground font-mono-nums w-4 text-right">{count}</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}